import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { IconPencil } from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { useAdminConversationDetail } from 'src/api/queries/chatAdmin/useAdminConversationDetail';
import { buildMessageSegments } from 'src/hooks/useChat';
import { MessageBubble } from 'src/app/chat/MessageBubble';
import { formatTranscriptTimestamp } from 'src/app/chat/transcript';
import { ChatMessageDto } from 'src/api/FiretrolApiClient';
import { Modal } from 'src/design/molecules/Modal';
import {
  formatReplyCost,
  reasoningEffortLabel,
} from 'src/routes/Settings/AIAssistant/replyUsage';
import { formatNumber, getErrorMessage } from 'src/utils';

export interface IConversationDetailModalProps {
  conversationId: string | null;
  onClose: () => void;
  /** Scrolls this message into view once the transcript renders and rings it. */
  focusMessageId?: string | null;
}

const FOCUS_RING_STYLE: React.CSSProperties = {
  boxShadow: '0 0 0 2px var(--mantine-color-blue-6)',
  borderRadius: 'var(--mantine-radius-md)',
  padding: 'var(--mantine-spacing-xs)',
};

const SUPERSEDED_STYLE: React.CSSProperties = {
  border: '1px dashed var(--mantine-color-default-border)',
  backgroundColor: 'var(--mantine-color-default-hover)',
  borderRadius: 'var(--mantine-radius-md)',
  padding: 'var(--mantine-spacing-xs)',
};

const replyUsageParts = (message: ChatMessageDto): string[] => {
  const parts = message.model ? [message.model] : [];
  if (!message.reasoningEffort) return [...parts, 'Effort not recorded'];
  const { reasoningTokens } = message;
  return [
    ...parts,
    `${reasoningEffortLabel(message.reasoningEffort)} effort`,
    `${formatNumber(reasoningTokens)} reasoning ${
      reasoningTokens === 1 ? 'token' : 'tokens'
    }`,
    `${formatReplyCost(message.estCostUsd)} est. cost`,
  ];
};

export const ConversationDetailModal: React.FC<
  IConversationDetailModalProps
> = ({ conversationId, onClose, focusMessageId }) => {
  const {
    data: conversation,
    isLoading,
    error,
  } = useAdminConversationDetail(conversationId);
  const focusRef = useRef<HTMLDivElement>(null);
  // Mantine reports the enter transition only when `opened` flips after
  // mount; a modal mounted already open starts out entered.
  const [entered, setEntered] = useState(!!conversationId);

  // The modal slides in with a transform, so positions measured before its
  // enter transition ends are off; scroll only once it has entered and the
  // transcript has loaded, whichever comes last.
  useEffect(() => {
    if (entered && focusMessageId && conversation) {
      focusRef.current?.scrollIntoView({ block: 'center' });
    }
  }, [entered, focusMessageId, conversation]);

  return (
    <Modal
      opened={!!conversationId}
      onClose={onClose}
      title={conversation?.title || 'Conversation'}
      size="lg"
      onEnterTransitionEnd={() => setEntered(true)}
      onExitTransitionEnd={() => setEntered(false)}
    >
      {isLoading && <Loader size="sm" />}
      {!isLoading && error && (
        <Alert color="red" title="Something went wrong">
          {getErrorMessage(error)}
        </Alert>
      )}
      {!isLoading && conversation && (
        <Stack gap="md">
          {conversation.messages
            .filter((m) => m.role !== 'system')
            .map((message) => {
              const focused = message.id === focusMessageId;
              const superseded = !!message.supersededAt;
              return (
                <Box
                  key={message.id}
                  ref={focused ? focusRef : undefined}
                  data-message-id={message.id}
                  data-focused={focused || undefined}
                  data-superseded={superseded || undefined}
                  style={{
                    ...(superseded ? SUPERSEDED_STYLE : undefined),
                    ...(focused ? FOCUS_RING_STYLE : undefined),
                  }}
                >
                  {message.supersededAt && (
                    <Group
                      gap={4}
                      wrap="nowrap"
                      mb={4}
                      justify={
                        message.role === 'user' ? 'flex-end' : 'flex-start'
                      }
                    >
                      <IconPencil
                        size={14}
                        aria-hidden
                        style={{
                          flexShrink: 0,
                          color: 'var(--mantine-color-dimmed)',
                        }}
                      />
                      <Text size="xs" c="dimmed">
                        Replaced by a resend ·{' '}
                        {/* The generated client types dates as Date but returns the raw JSON string. */}
                        {formatTranscriptTimestamp(
                          new Date(message.supersededAt),
                        )}
                      </Text>
                    </Group>
                  )}
                  {message.role === 'assistant' && (
                    <Text size="xs" c="dimmed" mb={4} data-testid="reply-usage">
                      {replyUsageParts(message).join(' · ')}
                    </Text>
                  )}
                  <MessageBubble
                    message={{
                      id: message.id,
                      role: message.role,
                      segments: buildMessageSegments(message),
                      createdAt: message.createdAt,
                      feedback: message.feedback ?? undefined,
                    }}
                    readOnly
                    onRefClick={onClose}
                  />
                </Box>
              );
            })}
        </Stack>
      )}
    </Modal>
  );
};
